import { assertNever } from "@redotech/util/type";
import { HsaFlow, HsaFlowNode, HsaFlowNodeType, HsaNodeId } from "./hsa-flow";

export enum HsaFlowStepKind {
  GROUP = "group",
  CONTENT = "content",
}

// Funnel analytics step kinds: the flow's own kinds plus the bucket for
// handles seen in events but absent from the active flow.
export enum HsaFunnelStepKind {
  GROUP = "group",
  CONTENT = "content",
  OTHER = "other",
}

// A renderable step of a flow (question group or content screen) in canonical
// display order, independent of any one shopper's path.
export interface HsaFlowStep {
  handle: string;
  label: string;
  kind: HsaFlowStepKind;
}

/**
 * Lists a flow's renderable steps in canonical order: a depth-first walk from
 * the root that follows a condition's matching edge before its non-matching
 * edge, emitting each GROUP / CONTENT node the first time it is reached.
 * Conditions and knockouts are not steps. Converging branches are listed
 * once; an empty flow (`rootId === null`) lists nothing.
 *
 * This is the order funnel analytics displays steps in. It is deliberately
 * not a shopper's per-session path index (which varies by branch), so every
 * handle has exactly one position here.
 */
export function listHsaFlowSteps(flow: HsaFlow): HsaFlowStep[] {
  const nodeById = new Map<HsaNodeId, HsaFlowNode>();
  for (const node of flow.nodes) {
    nodeById.set(node.nodeId, node);
  }
  const steps: HsaFlowStep[] = [];
  const visited = new Set<HsaNodeId>();
  const visit = (nodeId: HsaNodeId | null): void => {
    if (nodeId === null || visited.has(nodeId)) {
      return;
    }
    visited.add(nodeId);
    const node = nodeById.get(nodeId);
    if (!node) {
      return;
    }
    switch (node.nodeType) {
      case HsaFlowNodeType.GROUP: {
        steps.push({
          handle: node.groupHandle,
          label: node.title ?? node.groupHandle,
          kind: HsaFlowStepKind.GROUP,
        });
        visit(node.nextId);
        return;
      }
      case HsaFlowNodeType.CONTENT: {
        steps.push({
          handle: node.contentHandle,
          label: node.heading,
          kind: HsaFlowStepKind.CONTENT,
        });
        visit(node.nextId);
        return;
      }
      case HsaFlowNodeType.CONDITION: {
        visit(node.matchingNextId);
        visit(node.nonMatchingNextId);
        return;
      }
      case HsaFlowNodeType.KNOCKOUT: {
        return;
      }
      default: {
        assertNever(node);
      }
    }
  };
  visit(flow.rootId);
  return steps;
}
